/**
 * The trades and the money, for the close-out pop-up — ADR 0273.
 *
 * Two of the five questions: "are all of the trades correct (and it needs to
 * show them what might not be)" and "is the estimate amount correct". The
 * orders and expenses are in closeoutOrders.ts; `closeoutReview` in
 * closeout.ts puts all five together.
 */

import type { Balance } from "./balance.js";
import { signedChangeOrderTotal } from "./changeOrder.js";
import type {
  CloseoutInput,
  CloseoutKey,
  CloseoutOption,
  CloseoutQuickbooks,
  CloseoutSection,
  QuickbooksBillKind,
} from "./closeout.js";
import type { LadderTotals } from "./ladder.js";
import { addCents, cents, formatMoney, type Cents } from "./money.js";
import { tradeNames } from "./tradeProgress.js";

/* ------------------------------------------------------------- helpers */

export const QUESTION: Record<CloseoutKey, string> = {
  trades: "Are all of the trades correct?",
  amount: "Is the estimate amount correct?",
  work_orders: "Are the work orders accurate and complete?",
  material_orders: "Are the material orders accurate?",
  expenses: "Are the other expenses accurate and complete?",
};

export function section(key: CloseoutKey): CloseoutSection {
  return { key, question: QUESTION[key], facts: [], warnings: [] };
}

export const norm = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase();

export const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n} ${n === 1 ? one : many}`;

export function optionName(
  option: CloseoutOption,
  index: number,
  count: number,
): string {
  const own = option.estimate.name?.trim();
  if (own) return own;
  return count > 1 ? `Estimate ${index + 1}` : "The estimate";
}

const TROUBLE = new Set(["failed", "stranded"]);

export type Bill = CloseoutQuickbooks["bills"][number];

export function billFor(
  qb: CloseoutQuickbooks,
  kind: QuickbooksBillKind,
  id: string | undefined,
): Bill | undefined {
  return qb.bills.find((b) => b.sourceKind === kind && b.sourceId === id);
}

const failed = (what: string, error: string | null) =>
  `${what} failed${error ? ` (${error})` : ""}.`;

/** One line about a bill that is not safely in QuickBooks, or null. */
export function billTrouble(
  bill: Bill | undefined,
  what: string,
  expected: boolean,
): string | null {
  if (!bill) return expected ? `${what} has no QuickBooks bill.` : null;
  if (TROUBLE.has(bill.state))
    return failed(`${what}: the QuickBooks bill`, bill.lastError);
  if (bill.state === "pending")
    return `${what}: the QuickBooks bill has not gone yet.`;
  return null;
}

/** ", QuickBooks bill B-12" after an order's line, or nothing. */
export function billNote(bill: Bill | undefined): string {
  if (!bill) return "";
  if (bill.state !== "sent") return `, QuickBooks bill ${bill.state}`;
  return bill.docNumber
    ? `, QuickBooks bill ${bill.docNumber}`
    : ", in QuickBooks";
}

/* -------------------------------------------------------------- trades */

/** Every trade sold on the job: estimates, their lines, change orders. */
function soldTrades(options: readonly CloseoutOption[]): Map<string, string> {
  const sold = new Map<string, string>();
  const note = (name: string | null | undefined) => {
    const key = norm(name);
    if (key && !sold.has(key)) sold.set(key, (name ?? "").trim());
  };
  for (const option of options) {
    note(option.estimate.trade);
    option.items
      .filter((item) => item.work_doing !== false)
      .forEach((item) => note(item.trade));
    option.changeOrders.forEach((co) => note(co.trade));
  }
  return sold;
}

/** One estimate's own trade, and its lines that disagree or have none. */
function estimateTrades(
  out: CloseoutSection,
  option: CloseoutOption,
  name: string,
): void {
  const trump = option.estimate.trade?.trim() ?? "";
  out.facts.push(`${name}: ${trump || "no trade set"}`);
  if (!trump) out.warnings.push(`${name} has no trade set.`);

  const doing = option.items.filter((item) => item.work_doing !== false);
  const blank = doing.filter((item) => !norm(item.trade)).length;
  if (blank > 0)
    out.warnings.push(`${name}: ${plural(blank, "line")} with no trade.`);
  if (!trump) return;

  const differing = new Map<string, number>();
  for (const item of doing) {
    const trade = (item.trade ?? "").trim();
    if (trade && norm(trade) !== norm(trump))
      differing.set(trade, (differing.get(trade) ?? 0) + 1);
  }
  for (const [trade, n] of differing)
    out.warnings.push(
      `${name}: ${plural(n, "line says", "lines say")} ${trade}, the estimate says ${trump}.`,
    );
}

const nowhere = (what: string, trade: string) =>
  `${what} is for ${trade}, which is on no estimate line.`;

/** Work orders for trades nobody sold, by crews not down for that trade. */
function workOrderTrades(
  out: CloseoutSection,
  input: CloseoutInput,
  sold: Map<string, string>,
): Set<string> {
  const covered = new Set<string>();
  for (const wo of input.options.flatMap((o) => o.workOrders)) {
    const what = `Work order #${wo.order_number}`;
    const can = input.crewTrades[wo.crew_id ?? ""] ?? [];
    for (const trade of tradeNames(wo.trades)) {
      covered.add(norm(trade));
      if (sold.size > 0 && !sold.has(norm(trade)))
        out.warnings.push(nowhere(what, trade));
      if (can.length > 0 && !can.some((c) => norm(c) === norm(trade)))
        out.warnings.push(
          `${what}: ${wo.crew?.name ?? "the crew"} is not recorded as doing ${trade}.`,
        );
    }
  }
  return covered;
}

/** Material orders and expenses carrying a trade nobody sold. */
function otherTrades(
  out: CloseoutSection,
  input: CloseoutInput,
  sold: Map<string, string>,
): void {
  const unsold = (trade: string) => sold.size > 0 && !sold.has(norm(trade));
  for (const mo of input.options.flatMap((o) => o.materialOrders)) {
    const trade = (mo.trade ?? "").trim();
    const what = `Material order #${mo.order_number}`;
    if (!trade) out.warnings.push(`${what} has no trade.`);
    else if (unsold(trade)) out.warnings.push(nowhere(what, trade));
  }
  for (const ex of input.options.flatMap((o) => o.expenses)) {
    const trade = (ex.trade ?? "").trim();
    if (trade && unsold(trade))
      out.warnings.push(
        nowhere(`Expense "${ex.name?.trim() || "unnamed"}"`, trade),
      );
  }
}

export function tradesSection(input: CloseoutInput): CloseoutSection {
  const out = section("trades");
  const count = input.options.length;
  input.options.forEach((option, index) =>
    estimateTrades(out, option, optionName(option, index, count)),
  );
  const sold = soldTrades(input.options);
  const covered = workOrderTrades(out, input, sold);
  if (!input.job.work_orders_internal) {
    for (const [key, trade] of sold)
      if (!covered.has(key))
        out.warnings.push(`${trade} is sold but no work order covers it.`);
  }
  otherTrades(out, input, sold);
  return out;
}

/* -------------------------------------------------------------- amount */

/** Each estimate's total, and change orders the customer has not signed. */
function estimateAmounts(
  out: CloseoutSection,
  input: CloseoutInput,
  ladders: readonly LadderTotals[],
): Cents {
  const count = input.options.length;
  input.options.forEach((option, index) => {
    const ladder = ladders[index];
    if (!ladder) return;
    const name = optionName(option, index, count);
    const changes =
      ladder.changeOrders !== 0
        ? `, including ${formatMoney(ladder.changeOrders)} of change orders`
        : "";
    out.facts.push(`${name}: ${formatMoney(ladder.grandTotal)}${changes}`);
    // Every change order is in the total the moment it is typed (ADR 0168);
    // what is not signed is the rest of them.
    const unsigned = cents(
      ladder.changeOrders - signedChangeOrderTotal(option.changeOrders),
    );
    if (unsigned !== 0)
      out.warnings.push(
        `${name}: ${formatMoney(unsigned)} of change orders not signed by the customer.`,
      );
  });
  const total = addCents(...ladders.map((l) => l.grandTotal));
  if (count > 1) out.facts.push(`All estimates: ${formatMoney(total)}`);
  return total;
}

/** The schedule, what has arrived, and what is still owed. */
function balanceLines(out: CloseoutSection, balance: Balance): void {
  out.facts.push(`Contract: ${balance.contractFormatted}`);
  for (const line of balance.lines)
    out.facts.push(
      `${line.label}: ${line.amountFormatted}, ${line.paid ? "paid" : "not paid"}`,
    );
  out.facts.push(`Collected: ${balance.collectedFormatted}`);
  out.facts.push(`Still owed: ${balance.outstandingFormatted}`);
  if (balance.outstanding <= 0) return;
  const coming =
    balance.onItsWay > 0
      ? `, ${balance.onItsWayFormatted} of it on its way`
      : "";
  out.warnings.push(
    `The customer still owes ${balance.outstandingFormatted}${coming}.`,
  );
}

/** The QuickBooks invoice, and whether it says what the contract says. */
function invoiceLines(
  out: CloseoutSection,
  invoice: CloseoutQuickbooks["invoice"],
  contract: Cents,
): void {
  if (!invoice) {
    out.warnings.push("There is no QuickBooks invoice for this job.");
    return;
  }
  const billed = invoice.sentAmountCents ?? invoice.amountCents;
  const number = invoice.docNumber ? ` ${invoice.docNumber}` : "";
  const amount = billed == null ? "no amount" : formatMoney(cents(billed));
  out.facts.push(`QuickBooks invoice${number}: ${amount}, ${invoice.state}`);
  if (TROUBLE.has(invoice.state))
    out.warnings.push(failed("The QuickBooks invoice", invoice.lastError));
  else if (invoice.state !== "sent")
    out.warnings.push("The QuickBooks invoice has not gone yet.");
  else if (billed != null && billed !== contract)
    out.warnings.push(
      `The QuickBooks invoice says ${formatMoney(cents(billed))}; the contract is ${formatMoney(contract)}.`,
    );
}

/** Payments QuickBooks recorded, and any it could not place. */
function paymentLines(out: CloseoutSection, qb: CloseoutQuickbooks): void {
  const applied = qb.payments.filter((p) => p.state === "applied");
  if (applied.length > 0)
    out.facts.push(
      `QuickBooks payments: ${formatMoney(
        addCents(...applied.map((p) => cents(p.amountCents))),
      )} across ${plural(applied.length, "payment")}`,
    );
  const unmatched = qb.payments.filter((p) => p.state === "unmatched").length;
  if (unmatched > 0)
    out.warnings.push(
      `${plural(unmatched, "QuickBooks payment")} could not be matched to a payment on the schedule.`,
    );
}

export function amountSection(
  input: CloseoutInput,
  ladders: readonly LadderTotals[],
): CloseoutSection {
  const out = section("amount");
  const total = estimateAmounts(out, input, ladders);
  if (input.balance) balanceLines(out, input.balance);
  invoiceLines(
    out,
    input.quickbooks.invoice,
    input.balance ? input.balance.contract : total,
  );
  paymentLines(out, input.quickbooks);
  return out;
}
